import { useEffect, useState } from 'react';
import { formatPence, penceToPounds, poundsToPence } from '../billing.ts';
import {
  billingApi,
  type PlatformBillingProduct,
  type PlatformBillingProductUpdate,
} from '../billingApi.ts';
import { message } from './Platform.tsx';
import '../billing.css';

interface Draft {
  name: string;
  pounds: string;
  stripePriceId: string;
  active: boolean;
}

const draftOf = (product: PlatformBillingProduct): Draft => ({
  name: product.name,
  pounds: penceToPounds(product.pricePence),
  stripePriceId: product.stripePriceId ?? '',
  active: product.active,
});

/**
 * Only the fields that moved. An unchanged price is not re-sent, so saving a
 * rename cannot quietly rewrite a price somebody else changed a minute ago.
 */
const changesOf = (
  product: PlatformBillingProduct,
  draft: Draft,
  pricePence: number,
): PlatformBillingProductUpdate => {
  const stripePriceId = draft.stripePriceId.trim() === '' ? null : draft.stripePriceId.trim();
  return {
    ...(draft.name.trim() !== product.name ? { name: draft.name.trim() } : {}),
    ...(pricePence !== product.pricePence ? { pricePence } : {}),
    ...(stripePriceId !== product.stripePriceId ? { stripePriceId } : {}),
    ...(draft.active !== product.active ? { active: draft.active } : {}),
  };
};

/**
 * The price list, per client per month, in pounds.
 *
 * **The price here and the price in Stripe are two records of one fact.** The
 * console shows owners this one; Stripe invoices them from the price its id
 * points at. Changing the amount here does not reprice the Stripe price — a
 * Stripe price is immutable — so a new amount needs a new Stripe price created
 * there and its id pasted here in the same save, or the two will disagree on
 * the next invoice. The editor says so beside the button rather than trusting
 * anybody to remember it.
 *
 * Retiring a product (unticking Active) takes it off sale for new choices. The
 * clients who already have it keep it; the API decides what that means for
 * their subscription.
 */
export const PlatformBilling = () => {
  const [rows, setRows] = useState<PlatformBillingProduct[] | null>(null);
  const [drafts, setDrafts] = useState<Readonly<Record<string, Draft>>>({});
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    void billingApi
      .platformBillingProducts()
      .then((response) => {
        setRows(response.products);
        setDrafts(Object.fromEntries(response.products.map((row) => [row.id, draftOf(row)])));
      })
      .catch((cause: unknown) => setError(message(cause)));
  };

  useEffect(load, []);

  const update = (id: string, patch: Partial<Draft>) =>
    setDrafts((current) => {
      const existing = current[id];
      return existing === undefined ? current : { ...current, [id]: { ...existing, ...patch } };
    });

  const save = async (product: PlatformBillingProduct, draft: Draft, pricePence: number) => {
    setBusy(product.id);
    setError(null);
    setStatus(null);
    try {
      const response = await billingApi.updatePlatformBillingProduct(
        product.id,
        changesOf(product, draft, pricePence),
      );
      setStatus(`Saved ${response.product.name}: ${formatPence(response.product.pricePence)}.`);
      load();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(null);
    }
  };

  if (error !== null && rows === null) return <p className="error-banner">{error}</p>;
  if (rows === null) return <p className="note">Loading prices…</p>;

  return (
    <section className="pf-stack">
      {error !== null && <p className="error-banner">{error}</p>}

      <div className="card">
        <div className="card-head">
          <h2>Prices</h2>
        </div>
        <div className="card-body">
          <p className="lead">
            Per client, per month, in pounds, excluding VAT. A new amount needs a new Stripe price:
            create it in Stripe and paste its id here in the same save, or the console and the
            invoice will disagree.
          </p>

          <div className="scroll">
            <table className="bl-editor">
              <thead>
                <tr>
                  <th scope="col">Product</th>
                  <th scope="col">Name</th>
                  <th scope="col">Price (£)</th>
                  <th scope="col">Stripe price id</th>
                  <th scope="col">Active</th>
                  <th scope="col">
                    <span className="sr-only">Save</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((product) => {
                  const draft = drafts[product.id] ?? draftOf(product);
                  const pricePence = poundsToPence(draft.pounds);
                  const changes =
                    pricePence === null ? {} : changesOf(product, draft, pricePence);
                  const dirty = Object.keys(changes).length > 0;
                  const invalid = pricePence === null || draft.name.trim() === '';
                  return (
                    <tr key={product.id} className={product.active ? undefined : 'pf-retired'}>
                      <th scope="row">
                        <span className="mono">{product.id}</span>
                        <span className="hint">{product.kind}</span>
                      </th>
                      <td>
                        <label className="sr-only" htmlFor={`bp-name-${product.id}`}>
                          Name of {product.id}
                        </label>
                        <input
                          id={`bp-name-${product.id}`}
                          type="text"
                          value={draft.name}
                          onChange={(event) => update(product.id, { name: event.target.value })}
                        />
                      </td>
                      <td>
                        <label className="sr-only" htmlFor={`bp-price-${product.id}`}>
                          Price of {product.id} in pounds
                        </label>
                        <input
                          id={`bp-price-${product.id}`}
                          className="bl-price-input"
                          type="text"
                          inputMode="decimal"
                          value={draft.pounds}
                          aria-invalid={pricePence === null}
                          aria-describedby={
                            pricePence === null ? `bp-price-error-${product.id}` : undefined
                          }
                          onChange={(event) => update(product.id, { pounds: event.target.value })}
                        />
                        {pricePence === null && (
                          <span className="hint" id={`bp-price-error-${product.id}`}>
                            Pounds and pence, like 29 or 29.50.
                          </span>
                        )}
                      </td>
                      <td>
                        <label className="sr-only" htmlFor={`bp-stripe-${product.id}`}>
                          Stripe price id of {product.id}
                        </label>
                        <input
                          id={`bp-stripe-${product.id}`}
                          className="mono"
                          type="text"
                          value={draft.stripePriceId}
                          placeholder="price_…"
                          spellCheck={false}
                          onChange={(event) =>
                            update(product.id, { stripePriceId: event.target.value })
                          }
                        />
                      </td>
                      <td>
                        <div className="td-check">
                          <label>
                            <input
                              type="checkbox"
                              checked={draft.active}
                              onChange={(event) =>
                                update(product.id, { active: event.target.checked })
                              }
                            />
                            <span className="sr-only">{product.id} is on sale</span>
                          </label>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={busy !== null || !dirty || invalid}
                          aria-busy={busy === product.id}
                          onClick={() =>
                            pricePence !== null && void save(product, draft, pricePence)
                          }
                        >
                          {busy === product.id ? 'Saving…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="status-inline" role="status">
            {status}
          </p>
        </div>
      </div>
    </section>
  );
};
